/*
 * EditorHost — owns the connection between the top-level page and the miniPaint
 * document running in a same-origin iframe.
 *
 * Why an iframe at all: miniPaint is not a library. Its maintainer states plainly
 * that library embedding "is not possible" (viliusle/miniPaint#169) — the bundle
 * self-initialises on `window load`, styles the page from <body> down, and needs
 * its own ~23-id HTML skeleton. A same-origin iframe is the only supported embed.
 *
 * Why the tools still live in the top-level page: ChatGPT Desktop does NOT discover
 * WebMCP tools registered inside an iframe, same-origin or not. So the page registers
 * the tools and the handlers reach *into* the frame through this host. Never move
 * tool registration into the frame.
 */

export const EditorState = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  FAILED: 'failed'
}

/** Globals miniPaint publishes on its window once booted (see its src/js/main.js). */
const REQUIRED_GLOBALS = ['Layers', 'State', 'AppConfig', 'FileOpen', 'FileSave']

export class EditorHost {
  /**
   * @param {object} [options]
   * @param {number} [options.slowAfterMs] when to warn that boot is taking a while.
   *   miniPaint pulls ~40 individual icon files, and on a cold App Builder CDN cache
   *   that measured ~30 s — so a short timeout must NOT be treated as a failure.
   * @param {number} [options.failAfterMs] the hard give-up point.
   */
  constructor ({ logger = console, slowAfterMs = 8000, failAfterMs = 90000 } = {}) {
    this.logger = logger
    this.slowAfterMs = slowAfterMs
    this.failAfterMs = failAfterMs
    this.state = EditorState.IDLE
    this.slow = false
    this.error = null
    this._frame = null
    this._readyPromise = null
    this._listeners = new Set()
  }

  get ready () { return this.state === EditorState.READY }

  /** The iframe's window. Throws if the editor is not ready — callers should check first. */
  get win () {
    if (!this._frame || !this._frame.contentWindow) throw new Error('Editor frame is not attached')
    return this._frame.contentWindow
  }

  status () {
    return {
      state: this.state,
      attached: !!this._frame,
      slow: this.slow,
      error: this.error ? this.error.message : null,
      version: this.ready ? this._safe(() => this.win.AppConfig && this.win.AppConfig.version) : null
    }
  }

  subscribe (listener) {
    this._listeners.add(listener)
    return () => this._listeners.delete(listener)
  }

  _emit () {
    for (const l of this._listeners) {
      try { l(this.status()) } catch (e) { this.logger.error('[editor] listener failed', e) }
    }
  }

  _safe (fn) { try { return fn() } catch { return null } }

  _setState (state, error = null) {
    this.state = state
    this.error = error
    this._emit()
  }

  /**
   * Attach to the iframe element and resolve once miniPaint has published its
   * globals. Idempotent for the same element.
   */
  attach (frameElement) {
    if (this._frame === frameElement && this._readyPromise) return this._readyPromise

    this._frame = frameElement
    this._setState(EditorState.LOADING)

    this._readyPromise = new Promise((resolve, reject) => {
      const started = Date.now()
      this.slow = false

      const poll = () => {
        if (this.state === EditorState.READY) return
        let win
        try { win = frameElement.contentWindow } catch (e) { win = null }
        // Cross-origin would throw here; same-origin is a hard requirement.
        if (win && REQUIRED_GLOBALS.every((g) => !!win[g])) {
          this.slow = false
          this._setState(EditorState.READY)
          this.logger.info(`[editor] miniPaint ready after ${Date.now() - started} ms`)
          return resolve(this)
        }

        const elapsed = Date.now() - started
        if (elapsed > this.failAfterMs) {
          const err = new Error(
            `miniPaint did not become ready within ${Math.round(this.failAfterMs / 1000)} s. ` +
            'Check that /minipaint/index.html and /minipaint/dist/bundle.js are served from the same origin.'
          )
          this._setState(EditorState.FAILED, err)
          return reject(err)
        }
        // Slow is not failed: keep polling and recover silently once it boots.
        if (!this.slow && elapsed > this.slowAfterMs) {
          this.slow = true
          this.logger.info('[editor] miniPaint is still loading (cold CDN cache fetches ~40 icon files)')
          this._emit()
        }
        setTimeout(poll, 100)
      }

      poll()
    })

    return this._readyPromise
  }

  detach () {
    this._frame = null
    this._readyPromise = null
    this._setState(EditorState.IDLE)
  }

  /** Await readiness. Tools call this before touching the document. */
  whenReady () {
    if (!this._readyPromise) return Promise.reject(new Error('Editor frame is not attached'))
    return this._readyPromise
  }
}

export const editorHost = new EditorHost()
